import { useCallback, useEffect, useRef, useState } from "react";
import { InteractionManager } from "react-native";

export const useCatalogCarouselState = ({
  navigation,
  isFocused,
  selectedTypeIndex,
}: {
  navigation: any;
  isFocused: boolean;
  selectedTypeIndex: number | null;
}) => {
  const carouselRef = useRef<any>(null);
  const [activeSlide, setActiveSlide] = useState(0);
  const [isLeaving, setIsLeaving] = useState(false);
  const [slide2Ready, setSlide2Ready] = useState(false);
  const isLeavingRef = useRef(false);
  const isTransitioningRef = useRef(false);

  useEffect(() => {
    isLeavingRef.current = isLeaving;
  }, [isLeaving]);

  useEffect(() => {
    const markLeaving = () => {
      isLeavingRef.current = true;
      setIsLeaving(true);
    };

    const markActive = () => {
      isLeavingRef.current = false;
      setIsLeaving(false);
    };

    const unsubBlur = navigation.addListener("blur", markLeaving);
    const unsubFocus = navigation.addListener("focus", markActive);
    const unsubBeforeRemove = navigation.addListener("beforeRemove", markLeaving);

    return () => {
      isLeavingRef.current = true;
      setIsLeaving(true);
      unsubBlur();
      unsubFocus();
      unsubBeforeRemove();
    };
  }, [navigation]);

  useEffect(() => {
    if (activeSlide === 0) setSlide2Ready(false);
  }, [activeSlide]);

  useEffect(() => {
    if (!isFocused) return;

    isLeavingRef.current = false;
    setIsLeaving(false);

    const sync = () => {
      const index = carouselRef.current?.getCurrentIndex?.();
      if (typeof index === "number") {
        setActiveSlide(index);
        if (index === 1) {
          if (!isTransitioningRef.current) setSlide2Ready(true);
        } else {
          setSlide2Ready(false);
        }
        return;
      }

      if (
        selectedTypeIndex != null &&
        activeSlide === 1 &&
        !isTransitioningRef.current
      ) {
        setSlide2Ready(true);
      }
    };

    sync();
    const id = setTimeout(sync, 120);
    return () => clearTimeout(id);
  }, [activeSlide, isFocused, selectedTypeIndex]);

  const goToFilters = useCallback(() => {
    setActiveSlide(1);
    setSlide2Ready(false);
    isTransitioningRef.current = true;
    carouselRef.current?.scrollTo({ index: 1, animated: true });
  }, []);

  const handleSnap = useCallback((index: number) => {
    setActiveSlide(index);

    if (index === 1) {
      isTransitioningRef.current = false;
      setSlide2Ready(false);

      let cancelled = false;

      const fallbackId = setTimeout(() => {
        if (!cancelled) setSlide2Ready(true);
      }, 120);

      const task = InteractionManager.runAfterInteractions(() => {
        if (cancelled) return;
        clearTimeout(fallbackId);
        setSlide2Ready(true);
      });

      return () => {
        cancelled = true;
        clearTimeout(fallbackId);
        // @ts-ignore
        task?.cancel?.();
      };
    }

    setSlide2Ready(false);
    isTransitioningRef.current = false;
  }, []);

  return {
    carouselRef,
    activeSlide,
    setActiveSlide,
    slide2Ready,
    setSlide2Ready,
    isLeavingRef,
    goToFilters,
    handleSnap,
  };
};
